package dev.lingvo.site.nav

import dev.lingvo.shared.Role
import dev.lingvo.site.i18n.tr

/**
 * The four words a workspace is administered by, written once.
 *
 * They name what somebody may do, so they are read far more often than they are set: on the member
 * table, in the invite form, on a pending invitation, in the role-change modal, on the invitation
 * acceptance page and in every toast that reports a change. Three surfaces had written their own
 * `private fun Role.label()` and a fourth was still printing `role.name.lowercase()`, which reaches
 * the reader as "translator" in every language and is one rename of a wire constant away from
 * rewriting the sentence a customer reads about their own access.
 *
 * It lives in `:kit/nav` beside [AppDestination] because a role is what the nav model gates on —
 * the same enum decides which destinations exist — and because both `:site` and any later frontend
 * need the same four words. `tr`, not `t`: half the call sites are toasts, `LSelectOption` lists
 * and modal titles built outside composition.
 */
fun Role.label(): String = when (this) {
    Role.OWNER -> tr("ds.nav.role_labels.owner", "Owner")
    Role.ADMIN -> tr("ds.nav.role_labels.admin", "Admin")
    Role.TRANSLATOR -> tr("ds.nav.role_labels.translator", "Translator")
    Role.VIEWER -> tr("ds.nav.role_labels.viewer", "Viewer")
}

/**
 * The same word where a sentence has already started — "Make Ada an admin?", "Ada is now admin".
 *
 * `label().lowercase()` is what four call sites did, and it is wrong in German (every noun is
 * capitalised) and in Turkish (`I` lowercases to `ı`, not `i`, and `lowercase()` without a locale
 * does not know that). A separate key lets a translator answer the two positions differently, and
 * lets a language that does not distinguish them answer both the same way.
 */
fun Role.labelInSentence(): String = when (this) {
    Role.OWNER -> tr("ds.nav.role_labels.sentence_owner", "owner")
    Role.ADMIN -> tr("ds.nav.role_labels.sentence_admin", "admin")
    Role.TRANSLATOR -> tr("ds.nav.role_labels.sentence_translator", "translator")
    Role.VIEWER -> tr("ds.nav.role_labels.sentence_viewer", "viewer")
}
